<script setup lang="ts">
// Dev-only floating panel. Only renders for mock-bidder sessions.
//
// Top row — Event lifecycle: cycle the in-memory auction between
// Open / Closing soon (warn pill) / Closed without touching the backend.
// Lets the design demo walk all three ContextStrip + post-close
// states from one place.
//
// Middle row — Charge: visible only when the auction is in a closed
// state and an invoice has loaded. Cycles invoice paymentStatus so the
// AutoChargeBanner + charge badge + receipt can be walked through.
//
// Bottom row — Session (Lane B): walk Anonymous / Signed-in / Signed-in
// with an expired card without re-running the sign-in page. Mock card
// list is seeded so the PaymentMethodsPage demo round-trips.
//
// Removable once real lifecycle + charge + auth flows are wired.
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { InvoicePaymentStatus, PublicEvent } from '@neon-one/auctions-types';

import { useSession, type BidderSnapshot, type PaymentMethodSnapshot } from '@/shell/stores/session';
import { useAuctionEvent } from '@/shell/stores/auctionEvent';
import { useMyBidsStore } from '@/features/my-bids';

const session = useSession();
const auctionEvent = useAuctionEvent();
const myBids = useMyBidsStore();

const isMock = computed(
  () => typeof session.bidder?.uuid === 'string' && session.bidder.uuid.startsWith('mock-bidder-'),
);

// Show the session row whenever no auth is wired locally — keep visible
// even when isMock is false (Anonymous) so the demo can sign back in.

type SessionKey = 'anonymous' | 'signed-in' | 'signed-in-expired-card' | 'real-auth';
const SESSION_LABEL: Record<SessionKey, string> = {
  'anonymous': 'Anon',
  'signed-in': 'Signed in',
  'signed-in-expired-card': 'Expired card',
  'real-auth': 'Real auth',
};
const SESSION_ORDER: SessionKey[] = ['anonymous', 'signed-in', 'signed-in-expired-card', 'real-auth'];

const currentSession = computed<SessionKey>(() => {
  if (!session.isAuthenticated) return 'anonymous';
  if (session.getJwt() !== null) return 'real-auth';
  const cards = session.bidder?.cards ?? [];
  if (cards.some((c) => c.expired)) return 'signed-in-expired-card';
  return 'signed-in';
});

function mockSnapshot(opts: { withExpiredCard: boolean }): BidderSnapshot {
  const baseCards: PaymentMethodSnapshot[] = opts.withExpiredCard
    ? [
        { id: 'card_1', brand: 'Visa', last4: '4242', expMonth: 6, expYear: 2024, isDefault: true, expired: true },
      ]
    : [
        { id: 'card_1', brand: 'Visa', last4: '4242', expMonth: 12, expYear: 2029, isDefault: true },
      ];
  return {
    uuid: 'mock-bidder-1',
    email: 'demo@neon.test',
    firstName: 'Demo',
    lastName: 'Bidder',
    phone: null,
    activated: true,
    hasPaymentMethod: !opts.withExpiredCard,
    storedCard: opts.withExpiredCard ? null : { brand: 'Visa', last4: '4242', expiration: '12/2029', methodType: 'card' as const },
    tcAcceptedAt: '2026-05-19T00:00:00.000Z',
    activatedAt: '2026-05-19T00:00:00.000Z',
    cards: baseCards,
  };
}

async function setSession(next: SessionKey): Promise<void> {
  if (next === 'anonymous') {
    await session.signOut();
    return;
  }
  if (next === 'real-auth') {
    const slug = route.params.slug as string;
    await session.signOut();
    void router.push(`/${slug}/sign-in`);
    return;
  }
  const snapshot = mockSnapshot({ withExpiredCard: next === 'signed-in-expired-card' });
  session.setJwt(`mock-jwt-${snapshot.uuid}`, snapshot, auctionEvent.slug ?? undefined);
  // Re-fetch my-bids so the new mock-bidder snapshot's winning / won
  // rows populate immediately (the mock api response is gated on the
  // bidder uuid).
  await myBids.load();
}

type LifecycleKey = 'open' | 'closing-soon' | 'closed';
const LIFECYCLE_LABEL: Record<LifecycleKey, string> = {
  'open': 'Open',
  'closing-soon': 'Closing soon',
  'closed': 'Closed',
};
const LIFECYCLE_ORDER: LifecycleKey[] = ['open', 'closing-soon', 'closed'];

const currentLifecycle = computed<LifecycleKey | null>(() => {
  const ev = auctionEvent.event;
  if (!ev) return null;
  if (ev.status === 'closed') return 'closed';
  if (ev.status !== 'open' || !ev.closeAt) return null;
  const remainingMs = new Date(ev.closeAt).getTime() - Date.now();
  if (remainingMs <= 0) return null;
  return remainingMs <= 5 * 60_000 ? 'closing-soon' : 'open';
});

async function setLifecycle(next: LifecycleKey): Promise<void> {
  const patches: Record<LifecycleKey, Partial<PublicEvent>> = {
    'open': {
      status: 'open',
      closeAt: new Date(Date.now() + 5 * 24 * 60 * 60_000).toISOString(),
    },
    'closing-soon': {
      status: 'open',
      closeAt: new Date(Date.now() + 3 * 60_000).toISOString(),
    },
    'closed': {
      status: 'closed',
      closeAt: new Date(Date.now() - 60_000).toISOString(),
    },
  };
  auctionEvent.setMockEvent(patches[next]);
  // Pre-close uses winning/losing sections, post-close uses won/lost
  // — the mock fixture returns different shapes per status, so a
  // lifecycle flip needs a my-bids reload to swap sections.
  if (isMock.value) {
    await myBids.load();
  }
}

const isPostClose = computed(() => {
  return auctionEvent.event?.status === 'closed';
});
const chargeVisible = computed(() => isPostClose.value && myBids.invoice != null);

const CHARGE_CYCLE: InvoicePaymentStatus[] = ['paid', 'processing', 'failed'];
const CHARGE_LABEL: Record<InvoicePaymentStatus, string> = {
  pending: 'Pending',
  processing: 'Processing',
  paid: 'Paid',
  failed: 'Declined',
  refunded: 'Refunded',
  partially_refunded: 'Partially Refunded',
  // ADR-012 §4 — every lot left the invoice and nothing was collected.
  void: 'Void',
};
const currentCharge = computed<InvoicePaymentStatus>(() => myBids.invoice?.paymentStatus ?? 'paid');

function setCharge(next: InvoicePaymentStatus): void {
  if (!myBids.invoice) return;
  myBids.setMockInvoice({ ...myBids.invoice, paymentStatus: next });
}

// Lane C1 — Bid + item state edges. Each toggle sets the URL
// `?demo=` flag so the various feature mocks (my-bids/api.ts,
// features/bid/store.ts mock-jwt path, WishlistPage no-bids
// injector) read a consistent source of truth on next load.
const route = useRoute();
const router = useRouter();

// GT1-442 (bid-void-design.md §6.1) — this toggle renders inside the bidder
// app, so both the label AND the `?demo=` key use the bidder-facing word
// "Removed". "Voided" is admin-only vocabulary and must not appear on any
// bidder surface, demo tooling included.
type BidEdgeKey = 'normal' | 'withdrawn' | 'removed' | 'max-tie';
const BID_EDGE_LABEL: Record<BidEdgeKey, string> = {
  'normal': 'Normal',
  'withdrawn': 'Withdrawn',
  'removed': 'Removed',
  'max-tie': 'Max tie',
};
const BID_EDGE_ORDER: BidEdgeKey[] = ['normal', 'withdrawn', 'removed', 'max-tie'];

const currentBidEdge = computed<BidEdgeKey>(() => {
  const demo = route.query.demo;
  if (demo === 'withdrawn' || demo === 'removed' || demo === 'max-tie') return demo;
  return 'normal';
});

async function setBidEdge(next: BidEdgeKey): Promise<void> {
  const query = { ...route.query };
  if (next === 'normal') {
    delete query.demo;
  } else {
    query.demo = next;
  }
  await router.replace({ path: route.path, query });
  // Reload my-bids so the moderation overrides + section state
  // pick up the new demo flag without a hard navigation.
  if (typeof session.bidder?.uuid === 'string' && session.bidder.uuid.startsWith('mock-bidder-')) {
    await myBids.load();
  }
}

// Lane A — checkout mode demo. Three modes per the design guide:
// Mode 1 (default): card on file + autocharge — invoice auto-paid,
// bidder lands directly on /receipt from the PayBanner.
// Mode 2 (`?demo=card-no-autocharge`): card on file, no autocharge —
// bidder hits SelfCheckoutPage with their card preselected.
// Mode 3 (`?demo=no-card`): no card at registration — SelfCheckoutPage
// renders the inline NeonPayCardField for the bidder to add a card.
type CheckoutModeKey = 'autocharge' | 'card-no-autocharge' | 'no-card';
const CHECKOUT_MODE_LABEL: Record<CheckoutModeKey, string> = {
  'autocharge': 'Auto-charged',
  'card-no-autocharge': 'Manual checkout',
  'no-card': 'Add card',
};
const CHECKOUT_MODE_ORDER: CheckoutModeKey[] = [
  'autocharge',
  'card-no-autocharge',
  'no-card',
];

const currentCheckoutMode = computed<CheckoutModeKey>(() => {
  const demo = route.query.demo;
  if (demo === 'card-no-autocharge' || demo === 'no-card') return demo;
  return 'autocharge';
});

async function setCheckoutMode(next: CheckoutModeKey): Promise<void> {
  const query = { ...route.query };
  if (next === 'autocharge') {
    delete query.demo;
  } else {
    query.demo = next;
  }
  await router.replace({ path: route.path, query });
  // Reload my-bids so api.ts's readCheckoutDemo() picks up the new
  // ?demo= flag (it flips bidder.hasPaymentMethod + invoice.paymentStatus
  // so PayBanner + BidCard render the right mode immediately).
  if (typeof session.bidder?.uuid === 'string' && session.bidder.uuid.startsWith('mock-bidder-')) {
    await myBids.load();
  }
}
</script>

<template>
  <div
    v-if="isMock || !session.isAuthenticated"
    class="demo-toggle"
    data-testid="demo-state-toggle"
  >
    <div
      v-if="isMock"
      class="demo-toggle__row"
    >
      <span class="demo-toggle__label">Demo · auction</span>
      <div
        class="demo-toggle__buttons"
        role="group"
        aria-label="Cycle auction lifecycle"
      >
        <button
          v-for="key in LIFECYCLE_ORDER"
          :key="key"
          type="button"
          :class="['demo-toggle__btn', currentLifecycle === key && 'demo-toggle__btn--active']"
          :aria-pressed="currentLifecycle === key"
          @click="setLifecycle(key)"
        >
          {{ LIFECYCLE_LABEL[key] }}
        </button>
      </div>
    </div>

    <div
      v-if="isPostClose && isMock"
      class="demo-toggle__row"
      data-testid="demo-toggle__checkout"
    >
      <span class="demo-toggle__label">Demo · checkout</span>
      <div
        class="demo-toggle__buttons"
        role="group"
        aria-label="Cycle checkout mode"
      >
        <button
          v-for="key in CHECKOUT_MODE_ORDER"
          :key="key"
          type="button"
          :class="['demo-toggle__btn', currentCheckoutMode === key && 'demo-toggle__btn--active']"
          :aria-pressed="currentCheckoutMode === key"
          @click="setCheckoutMode(key)"
        >
          {{ CHECKOUT_MODE_LABEL[key] }}
        </button>
      </div>
    </div>

    <div
      v-if="chargeVisible"
      class="demo-toggle__row"
    >
      <span class="demo-toggle__label">Demo · charge</span>
      <div
        class="demo-toggle__buttons"
        role="group"
        aria-label="Cycle invoice payment status"
      >
        <button
          v-for="status in CHARGE_CYCLE"
          :key="status"
          type="button"
          :class="['demo-toggle__btn', currentCharge === status && 'demo-toggle__btn--active']"
          :aria-pressed="currentCharge === status"
          @click="setCharge(status)"
        >
          {{ CHARGE_LABEL[status] }}
        </button>
      </div>
    </div>

    <div class="demo-toggle__row">
      <span class="demo-toggle__label">Demo · session</span>
      <div
        class="demo-toggle__buttons"
        role="group"
        aria-label="Cycle session state"
      >
        <button
          v-for="key in SESSION_ORDER"
          :key="key"
          type="button"
          :class="['demo-toggle__btn', currentSession === key && 'demo-toggle__btn--active']"
          :aria-pressed="currentSession === key"
          :data-testid="`demo-state-toggle__session-${key}`"
          @click="setSession(key)"
        >
          {{ SESSION_LABEL[key] }}
        </button>
      </div>
    </div>

    <div
      class="demo-toggle__row"
      data-testid="demo-toggle__bid-edges"
    >
      <span class="demo-toggle__label">Demo · bid edges</span>
      <div
        class="demo-toggle__buttons"
        role="group"
        aria-label="Cycle bid + item state edges"
      >
        <button
          v-for="key in BID_EDGE_ORDER"
          :key="key"
          type="button"
          :class="['demo-toggle__btn', currentBidEdge === key && 'demo-toggle__btn--active']"
          :aria-pressed="currentBidEdge === key"
          @click="setBidEdge(key)"
        >
          {{ BID_EDGE_LABEL[key] }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/*
 * circuit-lint-allow demo-overlay — WHOLE BLOCK.
 *
 * This is not a product surface. It is the demo/QA state switcher that floats
 * over the running app behind a flag (see the script above) and never ships to
 * a bidder. It is deliberately styled OUTSIDE the design system: a neutral
 * dark chip that reads as "not part of the product" against every screen it
 * floats over, at a size no Circuit control offers. Tokenising it would make
 * scaffolding look like a feature — the raw colours, the 2px pressed-together
 * segmented buttons and the fixed offsets below are all that intent.
 *
 * If this overlay ever becomes a real product surface, it gets rebuilt on
 * Circuit components and tokens rather than having these values swapped.
 */
.demo-toggle {
  position: fixed;
  /* Above BottomNav + safe-area inset. */
  bottom: calc(80px + env(safe-area-inset-bottom));
  right: var(--app-space-16);
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: var(--app-space-8);
  padding: var(--app-space-8) var(--app-space-12);
  background: rgba(26, 26, 26, 0.92); /* circuit-lint-allow demo-overlay */
  color: #fff; /* circuit-lint-allow demo-overlay */
  border-radius: var(--app-radius-md);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18); /* circuit-lint-allow demo-overlay */
  font-size: var(--app-font-size-caption);
  line-height: var(--app-line-height-caption);
}
.demo-toggle__row {
  display: flex;
  flex-direction: column;
  gap: var(--app-space-4);
}
.demo-toggle__label {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.6); /* circuit-lint-allow demo-overlay */
  font-weight: var(--app-font-weight-medium);
}
.demo-toggle__buttons {
  display: flex;
  gap: 2px; /* circuit-lint-allow demo-overlay — segmented control, hairline seam */
  background: rgba(255, 255, 255, 0.12); /* circuit-lint-allow demo-overlay */
  border-radius: var(--app-radius-sm);
  padding: 2px; /* circuit-lint-allow demo-overlay — segmented control inset */
}
.demo-toggle__btn {
  all: unset;
  padding: var(--app-space-4) var(--app-space-8);
  cursor: pointer;
  border-radius: calc(var(--app-radius-sm) - 2px); /* circuit-lint-allow demo-overlay */
  color: rgba(255, 255, 255, 0.78); /* circuit-lint-allow demo-overlay */
  font-size: var(--app-font-size-caption);
  font-weight: var(--app-font-weight-medium);
}
.demo-toggle__btn:hover {
  color: #fff; /* circuit-lint-allow demo-overlay */
}
.demo-toggle__btn--active {
  background: rgba(255, 255, 255, 0.16); /* circuit-lint-allow demo-overlay */
  color: #fff; /* circuit-lint-allow demo-overlay */
}
</style>
